import ProductService from "../services/products-service";
import { getLastWidhthOrder } from "../util";
import type { BirkenstockSummary } from "../../components/BirkenstockLabel";

// Function to calculate custom size recommendation

export type InsoleLevel = "low" | "mid" | "high";
export type InsoleGender = "female" | "unisex";

// Set this manually while testing Birkenstock custom size recommendations.
// Good boundary test values with the current Birkenstock table: 244, 249, 250.
const DEBUG_BIRKENSTOCK_FOOT_LENGTH: number | undefined = undefined;

export function getRecommendedInsole(
  insoleConfig: any,
  level: InsoleLevel,
  gender: InsoleGender,
  brand?: string,
): any | null {
  if (!insoleConfig) return null;

  const brandBlock = brand ? insoleConfig.brands?.[brand]?.insoles : undefined;
  if (brandBlock) {
    const byGender = brandBlock[gender]?.[level];
    if (byGender) return byGender;
    const byUnisex = brandBlock.unisex?.[level];
    if (byUnisex) return byUnisex;
  }

  const defaultBlock = insoleConfig.insoles;
  if (defaultBlock && !Array.isArray(defaultBlock)) {
    const fromGender = defaultBlock[gender]?.[level];
    if (fromGender) return fromGender;
  }

  if (Array.isArray(defaultBlock)) {
    return defaultBlock.find((i: any) => i?.id?.toLowerCase() === level) ?? null;
  }

  return null;
}

export function getSizeScaleCategoriesAndMarker(primarySize?: number, secondarySize?: number) {
  let categories;
  let markerIndex = 2; // default center
  console.log("primary:", primarySize);
  console.log("second:", secondarySize);
  if (primarySize !== undefined && secondarySize && primarySize !== secondarySize) {
    const leftSize = Math.min(primarySize, secondarySize);
    const rightSize = Math.max(primarySize, secondarySize);
    categories = [
      { name: leftSize, value: leftSize }, // 0
      { name: "", value: 0 }, // 1
      { name: "", value: 0 }, // 2
      { name: "", value: 0 }, // 3
      { name: rightSize, value: rightSize }, // 4
    ];
    if (Number(primarySize) === leftSize) {
      markerIndex = 1;
    } else {
      markerIndex = 3;
    }
  } else if (primarySize !== undefined) {
    categories = [
      { name: "", value: 0 },
      { name: "", value: 0 },
      { name: primarySize, value: primarySize }, // 2
      { name: "", value: 0 },
      { name: "", value: 0 },
    ];
    markerIndex = 2;
  } else {
    categories = [
      { name: "", value: 0 },
      { name: "", value: 0 },
      { name: "", value: 0 },
      { name: "", value: 0 },
      { name: "", value: 0 },
    ];
    markerIndex = 2;
  }
  console.log("markerIndex:", markerIndex);
  return { categories, markerIndex };
}

export function getCustomRecommendations(products: any[], footLength: number, footWidth: number, currentScanner: any) {
  let filtredArray: any[] = [];

  // 1. Group products by model (categoryId or another unique property)
  const grouped = products.reduce<Record<string, any[]>>((acc, product: any) => {
    const key = product.categoryId || product.categoryName;
    if (!acc[key]) acc[key] = [];
    acc[key].push(product);
    return acc;
  }, {});

  // 2. For each group/model, apply the width logic
  Object.values(grouped).forEach((group: any[]) => {
    // Find the two variants (Narrow and Regular)
    const narrowProduct = group.find((p) => p.width_name?.toLowerCase() === "narrow");
    const regularProduct = group.find((p) => p.width_name?.toLowerCase() === "regular");
    const category = group[0].sizeChart ? group[0].sizeChart : group[0].categoryName?.toLowerCase().includes("sandal") ? "sandals" : "shoes";
    const sizeChart = currentScanner?.customSizeChart?.sizeChart?.[category];
    // The updated sandal table uses inclusive "from/to" ranges in whole millimeters.
    const matchesLength = (v: any) =>
      (category === "sandals" ? footLength >= v.LengthMin : footLength > v.LengthMin) && footLength <= v.LengthMax;

    // Find best size for each width
    let bestNarrow, bestRegular;
    if (narrowProduct && sizeChart?.Narrow) {
      // Get all narrow options sorted by EU
      const narrowOptions = Object.entries(sizeChart.Narrow)
        .map(([eu, v]: any) => ({ ...v, EU: eu, width: "Narrow" }))
        .sort((a, b) => Number(a.EU) - Number(b.EU));

      // Find min/max
      const minNarrow = narrowOptions[0];
      const maxNarrow = narrowOptions[narrowOptions.length - 1];

      // Find bestNarrow as before
      bestNarrow = narrowOptions.filter(matchesLength).sort((a, b) => Math.abs(footLength - a.IdealLength) - Math.abs(footLength - b.IdealLength))[0];

      // Handle too small
      if (!bestNarrow && footLength <= minNarrow.LengthMin) {
        bestNarrow = { ...minNarrow, outOfRange: "too_small" };
      }
      // Handle too big
      if (!bestNarrow && footLength > maxNarrow.LengthMax) {
        bestNarrow = { ...maxNarrow, outOfRange: "too_big" };
      }

      // Add secondary_eu if within 1mm of LengthMin or LengthMax
      if (bestNarrow) {
        const idx = narrowOptions.findIndex((opt) => opt.EU === bestNarrow.EU);
        let secondary_eu = undefined;
        if (Math.abs(footLength - bestNarrow.LengthMin) <= 1 && idx > 0) {
          secondary_eu = narrowOptions[idx - 1].EU;
        } else if (Math.abs(footLength - bestNarrow.LengthMax) <= 1 && idx < narrowOptions.length - 1) {
          secondary_eu = narrowOptions[idx + 1].EU;
        }
        if (secondary_eu) {
          bestNarrow.secondary_eu = secondary_eu;
        }
      }
    }
    if (regularProduct && sizeChart?.Regular) {
      // Get all regular options sorted by EU
      const regularOptions = Object.entries(sizeChart.Regular)
        .map(([eu, v]: any) => ({ ...v, EU: eu, width: "Regular" }))
        .sort((a, b) => Number(a.EU) - Number(b.EU));

      // Find min/max
      const minRegular = regularOptions[0];
      const maxRegular = regularOptions[regularOptions.length - 1];

      // Find bestRegular as before
      bestRegular = regularOptions.filter(matchesLength).sort((a, b) => Math.abs(footLength - a.IdealLength) - Math.abs(footLength - b.IdealLength))[0];

      // Handle too small
      if (!bestRegular && footLength <= minRegular.LengthMin) {
        bestRegular = { ...minRegular, outOfRange: "too_small" };
      }
      // Handle too big
      if (!bestRegular && footLength > maxRegular.LengthMax) {
        bestRegular = { ...maxRegular, outOfRange: "too_big" };
      }

      // Add secondary_eu if within 1mm of LengthMin or LengthMax
      if (bestRegular) {
        const idx = regularOptions.findIndex((opt) => opt.EU === bestRegular.EU);
        let secondary_eu = undefined;
        if (Math.abs(footLength - bestRegular.LengthMin) <= 1 && idx > 0) {
          secondary_eu = regularOptions[idx - 1].EU;
        } else if (Math.abs(footLength - bestRegular.LengthMax) <= 1 && idx < regularOptions.length - 1) {
          secondary_eu = regularOptions[idx + 1].EU;
        }
        if (secondary_eu) {
          bestRegular.secondary_eu = secondary_eu;
        }
      }
    }
    // Keep the outer width limits open for very narrow/wide feet. Regular leads in overlaps.
    const recommendations: any[] = [];
    if (bestRegular && footWidth >= bestRegular.WidthMin) recommendations.push(bestRegular);
    if (bestNarrow && footWidth <= bestNarrow.WidthMax) recommendations.push(bestNarrow);

    // Only push the correct product(s) for this model
    recommendations.forEach((rec) => {
      const product = rec.width === "Narrow" ? narrowProduct : regularProduct;
      if (!product) return;
      let size = {
        eu: rec?.EU || "",
        uk: rec?.UK || "",
        us: rec?.US || "",
        secondary_eu: rec?.secondary_eu || "",
        secondary_uk: rec?.secondary_uk || "",
        secondary_us: rec?.secondary_us || "",
      };
      filtredArray.push({
        id: product.categoryId || product.categoryName || "unknown",
        name: product.categoryName || product.name || "",
        color: product.color || "",
        products: {
          ...product,
          size,
          recommendedSizes: recommendations,
          leading_system: "eu",
        },
      });
    });
  });
  console.log("Custom recommendations:", filtredArray);
  return filtredArray;
}

function getBirkenstockSizeSummary(recommendation: any): BirkenstockSummary["sandal"] {
  const product = recommendation?.products;
  const narrowAlternative = product?.width_name?.toLowerCase() === "regular"
    ? product.recommendedSizes?.find((option: any) => option.width === "Narrow" && option.EU !== product.size?.eu)
    : undefined;

  return {
    size: product?.size?.eu || "",
    width: product?.width_name || "",
    secondarySize: product?.size?.secondary_eu || "",
    ...(narrowAlternative && { narrowAlternative: { size: narrowAlternative.EU, width: "Narrow" as const } }),
  };
}

export const getMatchedproducts = (
  setAllProducts: (product: any) => void,
  setMatchedProducts: (product: any) => void,
  setInitialMatchedProducts: (product: any) => void,
  lastwidthcategoryMode: string,
  customer_uuid: string,
  attributeName: any,
  gender?: string,
  safetyClass?: any,
  storeID?: string,
  showPartnerProducts?: boolean,
  showZeroStockProducts?: boolean,
  currentScanner?: any,
  currentMeasurements?: any,
  onComplete?: () => void,
  setBirkenstockSummary?: (summary: any) => void,
) => {
  let filtredArray: any[] = [];

  // Inject your custom logic here
  if (currentScanner?.partner?.toLowerCase() === "birkenstock" && currentScanner?.customSizeChart) {
    // Custom size chart logic for Birkenstock
    const products = currentScanner?.customSizeChart?.products;
    const measuredFootLength = Math.max(currentMeasurements?.measurements?.[0]?.measurements?.length_foot ?? 0, currentMeasurements?.measurements?.[1]?.measurements?.length_foot ?? 0);
    const footLength = Math.round(DEBUG_BIRKENSTOCK_FOOT_LENGTH ?? measuredFootLength);
    const footWidth = Math.round(Math.max(currentMeasurements?.measurements?.[0]?.measurements?.width_ball ?? 0, currentMeasurements?.measurements?.[1]?.measurements?.width_ball ?? 0));
    if (DEBUG_BIRKENSTOCK_FOOT_LENGTH) {
      console.log("Birkenstock debug foot length override:", DEBUG_BIRKENSTOCK_FOOT_LENGTH, "measured:", measuredFootLength);
    }
    console.log("footLength:", footLength, "footWidth:", footWidth);

    const filtredArray = getCustomRecommendations(products, footLength, footWidth, currentScanner);

    // Werte aus gemessenen Daten
    const leftLength = Math.round(currentMeasurements?.measurements?.[0]?.measurements?.length_foot ?? 0);
    const rightLength = Math.round(currentMeasurements?.measurements?.[1]?.measurements?.length_foot ?? 0);
    const leftWidth = Math.round(currentMeasurements?.measurements?.[0]?.measurements?.width_ball ?? 0);
    const rightWidth = Math.round(currentMeasurements?.measurements?.[1]?.measurements?.width_ball ?? 0);

    // Empfohlene Größen/Weiten für Sandale und Halbschuh aus den Recommendations extrahieren
    const sandalRec = filtredArray.find(
      (rec: any) =>
        rec.products?.sizeChart === "sandals" ||
        (!rec.products?.sizeChart && (rec.id === "open-shoes" || rec.products?.name?.toLowerCase().includes("sandal") || rec.name?.toLowerCase().includes("sandal"))),
    );

    const shoeRec = filtredArray.find(
      (rec: any) =>
        rec.products?.sizeChart === "shoes" ||
        (!rec.products?.sizeChart && rec !== sandalRec && (rec.id === "low-shoes" || rec.name?.toLowerCase() === "shoes" || rec.products?.name?.toLowerCase().startsWith("shoe"))),
    );

    console.log("UUID:", customer_uuid);

    const summary: BirkenstockSummary = {
      sandal: getBirkenstockSizeSummary(sandalRec),
      shoe: getBirkenstockSizeSummary(shoeRec),
      left: {
        length: leftLength,
        width: leftWidth,
      },
      right: {
        length: rightLength,
        width: rightWidth,
      },
      scanId: customer_uuid.slice(-10),
    };



    // Jetzt kannst du summary global speichern, z.B.:
    if (setBirkenstockSummary) {
      setBirkenstockSummary(summary);
    }

    setAllProducts(products);
    setMatchedProducts(filtredArray);
    setInitialMatchedProducts(filtredArray);
    console.log("MatchedProducts (custom size chart):", filtredArray);
    if (onComplete) onComplete();
    return; // <-- Important: stop here, do not call the API!
  }

  const genderArray = gender === "d" ? ["u", "f", "m"] : ["u", gender];

  const data = showPartnerProducts
    ? {
        onefid: customer_uuid,
        lastwidthcategory_mode: lastwidthcategoryMode,
        gender: genderArray,
      }
    : {
        onefid: customer_uuid,
        lastwidthcategory_mode: lastwidthcategoryMode,
        gender: genderArray,
        store_id: storeID,
      };

  ProductService.getMatchingProducts(data)
    .then((response: any) => {
      //throw away empty and duplicated gtins
      let matchedProductGtins = response.results.matching.filter((p1: any) => !!p1.gtin).map((p1: any) => (p1.gtin ? p1.gtin : undefined));
      setAllProducts(response.results.products);

      response.results.products.map((product: any) => {
        product.details.map((detail: any) => {
          if (matchedProductGtins.includes(detail.gtin)) {
            // TODO: remove when checked everything is working as expected
            // matchedProductGtins.splice(matchedProductGtins.indexOf(detail.gtin), 1);
            let secondary_eu: any = response.results.matching.find((product: any) => product.gtin === detail.gtin).secondary_eu;
            let secondary_uk: any = response.results.matching.find((product: any) => product.gtin === detail.gtin).secondary_uk;
            let secondary_us: any = response.results.matching.find((product: any) => product.gtin === detail.gtin).secondary_us;
            console.log("product:", product.category_info.id);
            filtredArray.push({
              id: product.category_info.id,
              name: product.category_info.name,
              attributes: product[attributeName],
              color: product.color,
              products: {
                id: product.id,
                name: product.name,
                categoryId: product.category_info.id,
                categoryName: product.category_info.name,
                brand: product.brand,
                gtin: detail.gtin,
                mpn: product.mpn,
                price: detail.price,
                salePrice: detail.sale_price,
                color: product.color,
                storeID: detail.store_id,
                stock: detail.stock,
                projectedStock: detail.projected_stock,
                warehouseBin: detail.warehouse_bin,
                leatherType: product.leather_type,
                support: product.support,
                underground: product.underground,
                safety_class: product.safety_class,
                suitableOrthoAdaption: product.suitable_ortho_adaption,
                suitableOrthoInsole: product.suitable_ortho_insole,
                suitableStandardInsole: product.suitable_standard_insole,
                productGender: product.gender,
                application: product.application,
                ...(product.images[0] && { image_url: product.images[0].original }),
                ...(product.images[0] && { image_url: product.images[0].thumb }),
                size: {
                  eu: response.results.matching.find((product: any) => product.gtin === detail.gtin).eu,
                  uk: response.results.matching.find((product: any) => product.gtin === detail.gtin).uk,
                  us: response.results.matching.find((product: any) => product.gtin === detail.gtin).us,
                  ...(secondary_eu && { secondary_eu: secondary_eu }),
                  ...(secondary_uk && { secondary_uk: secondary_uk }),
                  ...(secondary_us && { secondary_us: secondary_us }),
                },
                ...(product.images.length > 0 && { image_url: product.images[0].original }),
                ...(product.images.length > 0 && { thumb_url: product.images[0].thumb }),
                description: product.description,
                last_width_category: product.last_width_category,
                width_name: product.width_name,
                msi: product.msi,
                leading_system: response.results.matching.find((product: any) => product.gtin === detail.gtin).leading_system,
                ...response.results.matching.find((product: any) => product.gtin === detail.gtin).leading_system,
              },
            });
          }
        });
      });

      // remove duplicated products based on gtin
      // var uniqueMatchedProducts: any[] = filtredArray.reduce((unique, o) => {
      //   if (!unique.some(obj => obj.products.gtin === o.products.gtin)) {
      //     unique.push(o);
      //   }
      //   return unique;
      // }, []);

      // filter matched products based on user safety class
      if (safetyClass && attributeName === "safety_class") filtredArray = filtredArray.filter((p: any) => p.attributes === safetyClass);

      // filter matched products based on storeID
      if (!showPartnerProducts && storeID) filtredArray = filtredArray.filter((p: any) => p.products.storeID === storeID);
      // filter matched products based on stock
      if (!showZeroStockProducts) filtredArray = filtredArray.filter((p: any) => p.products.stock !== 0);

      setMatchedProducts(filtredArray);
      setInitialMatchedProducts(filtredArray);
      // Call the onComplete callback if provided
      if (typeof onComplete === "function") onComplete();
    })

    .catch((e) => {
      setMatchedProducts([]);
      setInitialMatchedProducts([]);
      console.log("ERROR getMatchingProducts2: ", e);
      // Call the onComplete callback in case of an error as well
      if (onComplete) onComplete();
    });
};

export const getMatchedProductsByGTIN = (onefid: string, gtin: string): any => {
  return ProductService.getMatchingProductsByGTIN(onefid, gtin).then((res: any) => {
    return {
      id: res.matching_id,
      gtin: res.gtin,
      name: res.product_name,
      brand: res.brand,
      description: res.description,
      image_url: res.image_url,
      thumb_url: res.thumb_url,
      last_width_category: res.last_width_category,
      leading_system: res.leading_system,
      gender: res.gender,
      size: {
        eu: res.eu,
        uk: res.uk,
        us: res.us,
        secondary_eu: res.secondary_eu,
        secondary_uk: res.secondary_uk,
        secondary_us: res.secondary_us,
      },
    };
  });
};

export const sortProductsByStore = (matchedProducts: any[], widthCategory: any) => {
  const LastWidthOrderArray = getLastWidhthOrder(widthCategory);

  // Sort by last_width_category then by stock within each category
  return matchedProducts.sort((p1: any, p2: any) => {
    // sort by last_width_category based on its order in LastWidthOrderArray
    const categoryComparison = LastWidthOrderArray.indexOf(p1.last_width_category) - LastWidthOrderArray.indexOf(p2.last_width_category);

    // If same last_width_category, sort by stock (stock > 0 products first)
    if (categoryComparison === 0) {
      return (p2.stock !== 0 ? 1 : 0) - (p1.stock !== 0 ? 1 : 0);
    }

    return categoryComparison;
  });
};
